import { useRef, useState } from 'react';
import { Camera, Paperclip, Loader2 } from 'lucide-react';
import { MAX_PRILOH, PRIJIMANE, nahratPrilohu, odebratNahranou } from './prilohy';
import PrilohyMrizka from './PrilohyMrizka';

/**
 * Výběr příloh do formuláře — dvě tlačítka a pod nimi to, co je přiložené.
 *
 * **Vyfotit** otevře na telefonu rovnou fotoaparát (`capture`), **Přiložit**
 * galerii nebo soubory (víc najednou). Fotky se před odesláním zmenší
 * a HEIC převede na JPEG (`pripravitSoubor`). Nahrává se hned po výběru, takže
 * odeslání formuláře pak nečeká — formulář jen pošle `prilohy: [id…]`.
 *
 * Řízená komponenta: `prilohy` (pole z API) + `onChange`. `onNahravani(bool)`
 * dá vědět, že něco ještě letí — formulář do té doby nemá jít odeslat.
 */
export default function PrilohyVyber({ typ, prilohy, onChange, onNahravani, isDark, max = MAX_PRILOH, popisek = true, kompaktni = false }) {
    const fotakRef = useRef(null);
    const souborRef = useRef(null);
    const [letici, setLetici] = useState(0);
    const [chyba, setChyba] = useState(null);

    const zbyva = Math.max(0, max - prilohy.length - letici);

    const vybrano = async (e) => {
        const soubory = [...(e.target.files || [])];
        e.target.value = '';
        if (!soubory.length) return;
        setChyba(null);
        const davka = soubory.slice(0, zbyva);
        if (soubory.length > davka.length) setChyba(`Přiložit jde nejvýš ${max} souborů.`);

        setLetici(n => n + davka.length);
        onNahravani?.(true);
        let aktualni = prilohy;
        for (const f of davka) {
            try {
                const p = await nahratPrilohu(f, typ);
                aktualni = [...aktualni, p];
                onChange(aktualni);
            } catch (err) {
                setChyba(err.message);
            } finally {
                setLetici(n => n - 1);
            }
        }
        onNahravani?.(false);
    };

    const odebrat = (p) => {
        odebratNahranou(p.id);
        onChange(prilohy.filter(x => x.id !== p.id));
    };

    const hrana = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const tlacitko = {
        height: kompaktni ? 36 : 40, padding: '0 14px', borderRadius: 10, fontSize: 13.5, fontWeight: 600,
        border: `1px solid ${hrana}`, background: 'transparent', color: 'var(--text-primary)',
        display: 'inline-flex', alignItems: 'center', gap: 7, cursor: zbyva ? 'pointer' : 'not-allowed',
        opacity: zbyva ? 1 : 0.45, WebkitTapHighlightColor: 'transparent',
    };

    return (
        <div>
            {popisek && (
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '16px 0 6px' }}>
                    Fotky a soubory{prilohy.length ? ` · ${prilohy.length}` : ''}
                </p>
            )}
            <div className="flex flex-wrap gap-2">
                <button type="button" style={tlacitko} disabled={!zbyva} onClick={() => fotakRef.current?.click()}>
                    <Camera size={16} /> Vyfotit
                </button>
                <button type="button" style={tlacitko} disabled={!zbyva} onClick={() => souborRef.current?.click()}>
                    <Paperclip size={16} /> Přiložit
                </button>
                {letici > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                        <Loader2 size={14} className="animate-spin" /> Nahrávám {letici}…
                    </span>
                )}
            </div>
            <input ref={fotakRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={vybrano} />
            <input ref={souborRef} type="file" accept={PRIJIMANE} multiple className="hidden" onChange={vybrano} />

            {chyba && <p className="text-xs" style={{ color: '#EF4444', marginTop: 6 }}>{chyba}</p>}

            {prilohy.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <PrilohyMrizka prilohy={prilohy} isDark={isDark} onOdebrat={odebrat} velikostNahledu={72} />
                </div>
            )}
        </div>
    );
}
